import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import {
  useHasPermission,
  usePermissions,
} from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { PrecheckinWidgetProps } from './PrecheckinWidget.types';

import { usePrecheckinWidgetMFE, useWidgetContentObserver } from './hooks';
import styles from './PrecheckinWidget.module.css';

/**
 * PrecheckinWidget - MFE wrapper for PrecheckinWidget
 *
 * This feature loads the remote PrecheckinWidget MFE and passes
 * the required props. The MFE handles its own internal routing.
 *
 * Guard stack (applied in Train module overview page):
 * 1. User must be logged in
 * 2. Features - precheckinMfeEnabled must be enabled
 * 3. Permissions - ModuleTrain_View_Any required
 *
 */
const PrecheckinWidget = React.memo(
  ({
    isMessageWidget = false,
    geminiReservationId,
    className,
    onLoad,
  }: Readonly<PrecheckinWidgetProps>): React.ReactElement | null => {
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const { precheckinMfeEnabled } = useFlags();
    const navigate = useNavigate();

    const canViewPrecheckinWidget =
      useHasPermission('ModuleTrain_View_Any') && precheckinMfeEnabled;

    const widgetContainerRef = React.useRef<HTMLDivElement | null>(null);

    const navigateToCheckin = React.useCallback(
      (trainingId: string): void => {
        navigate(`/train/checkin/${trainingId}`);
      },
      [navigate]
    );

    const { RemoteComponent, loadError } = usePrecheckinWidgetMFE(
      canViewPrecheckinWidget,
      onLoad
    );

    const hasWidgetContent = useWidgetContentObserver({
      isMessageWidget,
      containerRef: widgetContainerRef,
      shouldObserve: Boolean(RemoteComponent),
    });

    if (!canViewPrecheckinWidget || loadError) {
      return null;
    }

    const loadingPlaceholder = (
      <div data-precheckin-loading-placeholder>
        <ContentLoader />
      </div>
    );

    const content = !RemoteComponent ? (
      loadingPlaceholder
    ) : (
      <React.Suspense fallback={loadingPlaceholder}>
        <RemoteComponent
          msalInstance={instance}
          user={authData}
          permissions={permissions}
          isMessageWidget={isMessageWidget}
          geminiReservationId={geminiReservationId}
          navigateToCheckin={navigateToCheckin}
        />
      </React.Suspense>
    );

    return (
      <>
        {!isMessageWidget && hasWidgetContent && (
          <h2 className={`heading-02 ${styles.precheckinWidgetTitle}`}>
            Needs your attention
          </h2>
        )}
        {className ? (
          <div className={className} ref={widgetContainerRef}>
            {content}
          </div>
        ) : (
          <div ref={widgetContainerRef}>{content}</div>
        )}
      </>
    );
  }
);

PrecheckinWidget.displayName = 'PrecheckinWidget';

export { PrecheckinWidget };
